import {
    es,
    ca,
    fr,
    it,
    pt,
    enUS as en,
    enGB,
    uk,
    nl,
    de,
    sv as se,
    da,
} from "date-fns/locale";
import {
    CountryVersion,
    UTSLocale,
    CountrySlugs as AttrConstCS,
    Language,
    CountryCode,
} from "@smile2impress/attraction-constants";

import phoneCodes from "../configs/countries/phoneCodes.json";

export enum WindowStateSize {
    DESKTOP_SMALL_MAXIMUM_SIZE = 860,
    TABLET_MAXIMUM_SIZE = 768,
    MOBILE_MAXIMUM_WIDTH = 540,
}

export enum AppMode {
    DEV = "dev",
    PRELIVE = "prelive",
    LIVE = "live",
}

export enum BW_EVENT_TYPE {
    WIDGET_VIEWED = "booking.widgetViewed",
    DATE_TIME_SELECTED = "booking.widgetDateTimeSelected",
    LEAD_CREATED = "booking.widgetLeadCreated",
    BOOKING_SUBMITTED = "booking.widgetBookingSubmitted",
    BOOKING_RESCHEDULED = "booking.widgetBookingRescheduled",
}

export const PAGE_REVALIDATION_TIME = 600; // seconds

export const PAGES_WITHOUT_REGION_SERVICE = ["/blog"];

export const SHOW_LANGUAGE_QUESTION_EXCEPTIONS = [
    "/universalForm",
    "/bookingWidget",
    "/universalSelect",
    "/universalSelectLong",
    "/thanks",
    "/thanksText",
    "/dr-smile-universal-form",
    "/dr-smile-booking-widget",
    "/dr-smile-thanks",
    "/dr-smile-thanks-contact",
    "/dw-rescan",
    "/smajla-universal-form",
    "/smajla-booking-widget",
    "/smajla-thanks-contact",
];

export const SHOW_COOKIES_BANNER_EXCEPTIONS = [
    "/universal-form",
    "/ultra-form",
    "/booking-widget",
    "/dw-rescan",
    "/universal-select",
    "/universal-selectLong",
    "/thanks",
    "/thanks-text",
    "/dr-smile-universal-form",
    "/dr-smile-booking-widget",
    "/dr-smile-thanks",
    "/dr-smile-thanks-contact",
    "/dw-rescan",
    "/smajla-universal-form",
    "/smajla-booking-widget",
    "/smajla-thanks-contact",
    "/questionnaire-invisalign",
];

export const HIDE_SOCIAL_WIDGET = [
    "/blogPost",
    "/candidate",
    "/complains-formular",
    "/dr-smile-universal-form",
    "/dr-smile-booking-widget",
    "/dr-smile-thanks",
    "/dr-smile-thanks-contact",
    "/dw-rescan",
    "/smajla-universal-form",
    "/smajla-booking-widget",
    "/smajla-thanks-contact",
    "/smile-vision-offer",
    "/smile-vision-offer/[offerId]",
    "/question-form-cph",
];

export const AllowedHostsList = [
    "localhost",
    "smile2impress.com",
    "dev1.impress-global.com",
    "dev2.impress-global.com",
    "dev3.impress-global.com",
    "dev4.impress-global.com",
    "prelive.impress-global.com",
];

export const locales = {
    en,
    es,
    ca,
    fr,
    it,
    pt,
    uk,
    nl,
    de,
    se,
    da,
};

export type LocaleKeyType = keyof typeof locales;

export enum AuthStatuses {
    Initial,
    Pending,
    Success,
    Error,
}

export enum FetchingStatuses {
    Initial,
    Loading,
    Success,
    Error,
}

export enum LogoTheme {
    MONOCOLOR = "monocolor",
    BICOLOR = "bicolor",
}

export enum Theme {
    DARK = "dark",
    LIGHT = "light",
    SYT = "syt",
    SYTDark = "sytDark",
}

export enum CustomTheme {
    DRSMILE = "drSmile",
    SMAJLA = "smajla",
}

export const ThemeConfig = {
    headerBackground: "base.primary",
    linkColor: "text.primary",
    linkMoreMenuColor: "text.primary",
    linkActiveColor: "text.accent",
    iconColor: "text.primary",
    loginButtonColor: "secondary",
    appointmentButtonColor: "primary",
};

export enum Direction {
    LEFT = "left",
    RIGHT = "right",
}

export const WINDOW_SIZE = {
    DESKTOP_LARGE: 1440,
    DESKTOP: 1024,
    TABLET: 768,
    MOBILE: 375,
};

export const WINDOW_BREAKPOINTS = {
    DESKTOP: 1600,
    TABLET: 1366,
    MOBILE: 1024,
};

export enum Version {
    ITALY = "italy",
    SPAIN = "spain",
    PORTUGAL = "portugal",
    FRANCE = "france",
    UK = "uk",
}

export enum COOKIES {
    EXPERIMENTS = "impress_tgid",
    UUID = "impress_uuid",
    TRADEDOUBLER_ID = "impress_tduid",
    IS_EXPERIMENT_SET_UP = "impress_is_experiment_set_up",
    EXPERIMENT_GIFT = "impress_experiment_gift",
    IS_SMS_VERIFICATION_REQUIRED_SET_UP = "impress_is_sms_verification_required",
    IS_LEAD_SMS_VERIFICATION_REQUIRED_SET_UP = "impress_is_lead_sms_verification_required",
    OPEN_PAGE = "impress_open_page",
    SOURCE_PAGE = "impress_source_page",
    BOOKING_PAGE = "impress_booking-page",
    EMAIL = "impress_email",
    PHONE = "impress_phone",
    FIRST_NAME = "impress_firstname",
    LAST_NAME = "impress_lastname",
    CLINIC_ID = "impress_clinicid",
    CITY_ID = "impress_city_id",
    CITY_NAME = "impress_cityName",
    FIRST_NAME_BUFFER = "impress_buffer_firstname",
    LAST_NAME_BUFFER = "impress_buffer_lastname",
    EMAIL_BUFFER = "impress_buffer_email",
    PHONE_BUFFER = "impress_buffer_phone",
    FBP = "_fbp",
    FBC = "_fbc",
    PREFERRED_LANG = "impress_preferred_lang",
    PREFERRED_REGION = "impress_preferred_region",
    IMPRESS_CITY = "impress_city",
    CUSTOMER_UUID = "impress_customer_uuid",
    EXPERIMENT_NEW_WIDGET = "impress_tg",
    COOKIES_POLICY = "impress_cookies_policy",
    IS_ANALYSIS_COOKIE_ACTIVE = "impress_is_analysis_cookie_active",
    IS_FUNCTIONAL_COOKIE_ACTIVE = "impress_is_functional_cookie_active",
    IS_MARKETING_COOKIE_ACTIVE = "impress_is_marketing_cookie_active",
    VERIFICATION_TOKEN = "impress_verification_token",
    GCLID_SERVICE_COOKIE = "impress_gclid",
    RAKUTEN_SITE_ID_SERVICE_COOKIE = "impress_rakuten_site_id",
    DR_SMILE_SERVICE_IS_LEAD_FROM_DRSMILE_COOKIE = "impress_lead_from_dr_smile",
    SMAJLA_SERVICE_IS_LEAD_FROM_SMAJLA_COOKIE = "impress_lead_from_smajla",
    IS_PRIVACY_POLICY_CHECKED = "impress_is_privacy_policy_checked",
    IS_TYPEFORM_QUIZ_FINISHED = "impress_is_typeform_quiz_finished",
    NEXT_LOCALE = "NEXT_LOCALE",
    ACTION_POPUP = "impress_action_popup",
    BOOKING_FINISHED = "impress_booking_finished",
    GEO_LATITUDE = "impress_geo_latitude",
    GEO_LONGITUDE = "impress_geo_longitude",
    GEO_CITY = "impress_geo_city",
    GEO_CITY_ID = "impress_geo_city_id",
    GEO_SEARCH_COUNTRY = "impress_geo_search_country",
    GEO_BLOCK = "impress_geo_block",
    GEO_TIMEZONE = "impress_geo_timezone",
    TIKTOK_CLICK_ID = "impress_tiktok_click_id",
    /** Epoch-ms capture time of TIKTOK_CLICK_ID (see AdClickIdService), used to pick the most recently clicked ad platform. */
    TIKTOK_CLICK_ID_TS = "impress_tiktok_click_id_ts",
    SNAPCHAT_CLICK_ID = "impress_snapchat_click_id",
    /** Epoch-ms capture time of SNAPCHAT_CLICK_ID (see AdClickIdService), used to pick the most recently clicked ad platform. */
    SNAPCHAT_CLICK_ID_TS = "impress_snapchat_click_id_ts",
    REDDIT_CLICK_ID = "impress_reddit_click_id",
    /** Epoch-ms capture time of REDDIT_CLICK_ID (see AdClickIdService), used to pick the most recently clicked ad platform. */
    REDDIT_CLICK_ID_TS = "impress_reddit_click_id_ts",
    SPOTIFY_CLICK_ID = "impress_spotify_click_id",
    MICROSOFT_CLICK_ID = "impress_microsoft_click_id",
    CHATGPT_CLICK_ID = "impress_chatgpt_click_id",
    TWITTER_CLICK_ID = "impress_twitter_click_id",
    IMPRESS_AB_EXPERIMENT = "impress_ab_experiment",
    /** Set on marketing landings (e.g. Framer); read by Ultra Form for HS + GTM. */
    ULTRA_FORM_LANDING_EXPERIMENT_ID = "impress_ultra_form_experiment_id",
    DR_SMILE_REFERRER_PAGE = "drsmile-page-referrer",
    REFERRER_PAGE = "impress_page-referrer",
    DISABLE_GROWTHBOOK = "impress_disable_gb",
}

export enum DRSMILE_COOKIES {
    FIRST_NAME = "first",
    LAST_NAME = "last",
    EMAIL = "email",
    PHONE = "phone",
}

export enum FIELDS {
    FIRST_NAME = "firstName",
    LAST_NAME = "lastName",
    EMAIL = "email",
    PHONE = "phone",
    INSTAGRAM_USER = "instagramUser",
    BIRTH_DATE = "birthDate",
}

export enum LOCAL_STORAGE_KEYS {
    VID = "impress_vid",
    USER_FORM_DATA = "user_form_data",
    IMPRESS_AB_EXPERIMENT = "impress_ab_experiment",
}

export enum SESSION_STORAGE_KEYS {
    SESSION_ID = "impress_session_id",
    FORM_DATA = "user_form_data",
    REFERER = "referer",
    IMPRESS_TF_TO_BW_EXPERIMENT = "impress_tf_to_bw_experiment",
    IMPRESS_QUIZ_RESULT_OPTION = "impress_quiz_result_option",
}

export enum QUERY_STRING_KEYS {
    DESIGN_SYSTEM_FLAG = "design_system",
}

export const BaseUtmMarks = [
    "utm_source",
    "utm_campaign",
    "utm_medium",
    "utm_content",
    "utm_term",
    "utm_attraction",
];

export enum ContactTypes {
    PHONE = "phone",
    EMAIL = "email",
    CHAT = "chat",
}

export enum SocialTypes {
    WA = "wa",
    TG = "tg",
    FB = "fb",
    INSTAGRAM = "instagram",
    TIKTOK = "tiktok",
    LINKDIN = "linkdin",
    CHAT = "chat",
}

export enum QUIZ_RESULT_OPTION {
    ONE = 1,
    TWO = 2,
    THREE = 3,
    SIX = 6,
    SEVEN = 7,
    EIGHT = 8,
    ONE_TWO = 12,
    THREE_FOUR = 34,
    FIVE_SIX = 56,
    PARENTS = 101,
    TEENS = 102,
    PARENTS_COMPLETED = 111,
    TEENS_COMPLETED = 112,
}

export enum CountryRegion {
    denmark = "da",
    italy = "it",
    spain = "es",
    cat = "es",
    portugal = "pt",
    france = "fr",
    uk = "uk",
    ukraine = "ua",
    us = "us",
    nl = "nl",
    germany = "de",
    sweden = "se",
}

export const CountryRegionToCountrySlug = {
    [CountryRegion.italy]: AttrConstCS[UTSLocale.ITALY_IT],
    [CountryRegion.spain]: AttrConstCS[UTSLocale.SPAIN_ES],
    [CountryRegion.portugal]: AttrConstCS[UTSLocale.PORTUGAL_PT],
    [CountryRegion.france]: AttrConstCS[UTSLocale.FRANCE_FR],
    [CountryRegion.uk]: AttrConstCS[UTSLocale.GB_EN],
    [CountryRegion.ukraine]: AttrConstCS[UTSLocale.UKRAINE_UK],
    [CountryRegion.us]: AttrConstCS[UTSLocale.US_EN],
    [CountryRegion.nl]: AttrConstCS[UTSLocale.NL_NL],
    [CountryRegion.germany]: AttrConstCS[UTSLocale.GERMANY_DE],
    [CountryRegion.sweden]: AttrConstCS[UTSLocale.SWEDEN_SE],
    [CountryRegion.denmark]: AttrConstCS[UTSLocale.DENMARK_DK],
};

export const CountryCodeToVersion = {
    [CountryRegion.italy]: CountryVersion.ITALY,
    [CountryRegion.spain]: CountryVersion.SPAIN,
    [CountryRegion.portugal]: CountryVersion.PORTUGAL,
    [CountryRegion.france]: CountryVersion.FRANCE,
    [CountryRegion.uk]: CountryVersion.UK,
    [CountryRegion.ukraine]: CountryVersion.UKRAINE,
    [CountryRegion.us]: CountryVersion.US,
    [CountryRegion.nl]: CountryVersion.NL,
    [CountryRegion.germany]: CountryVersion.GERMANY,
    [CountryRegion.sweden]: CountryVersion.SWEDEN,
    [CountryRegion.denmark]: CountryVersion.SWEDEN,
};

export const CountryCodeToLanguage = {
    [CountryRegion.italy]: Language.ITALY,
    [CountryRegion.spain]: Language.SPAIN,
    [CountryRegion.portugal]: Language.PORTUGAL,
    [CountryRegion.france]: Language.FRANCE,
    [CountryRegion.uk]: Language.ENGLISH,
    [CountryRegion.ukraine]: Language.UKRAINE,
    [CountryRegion.us]: Language.ENGLISH,
    [CountryRegion.nl]: Language.DUTCH,
    [CountryRegion.germany]: Language.GERMANY,
    [CountryRegion.sweden]: Language.SWEDEN,
    [CountryRegion.denmark]: Language.ENGLISH,
};

export const UTSLocaleToCountryRegion = {
    [UTSLocale.SPAIN_ES]: CountryRegion.spain,
    [UTSLocale.ITALY_IT]: CountryRegion.italy,
    [UTSLocale.PORTUGAL_PT]: CountryRegion.portugal,
    [UTSLocale.US_EN]: CountryRegion.us,
    [UTSLocale.GB_EN]: CountryRegion.uk,
    [UTSLocale.FRANCE_FR]: CountryRegion.france,
    [UTSLocale.UKRAINE_UK]: CountryRegion.ukraine,
    [UTSLocale.NL_NL]: CountryRegion.nl,
    [UTSLocale.GERMANY_DE]: CountryRegion.germany,
    [UTSLocale.SWEDEN_SE]: CountryRegion.sweden,
    [UTSLocale.DENMARK_DK]: CountryRegion.denmark,
};

export const PARTNER_CLINICS = {
    [UTSLocale.SPAIN_ES]: "",
    [UTSLocale.PORTUGAL_PT]: "",
    [UTSLocale.FRANCE_FR]: "",
    [UTSLocale.UKRAINE_UK]: "",
    [UTSLocale.GB_EN]: "",
    [UTSLocale.ITALY_IT]: "",
    [UTSLocale.US_EN]: "Uniform Teeth Clinic",
};

export const DATE_FNS_LOCALES = {
    [UTSLocale.GB_EN]: enGB,
    [UTSLocale.US_EN]: en,
    [UTSLocale.SPAIN_ES]: es,
    [UTSLocale.FRANCE_FR]: fr,
    [UTSLocale.ITALY_IT]: it,
    [UTSLocale.PORTUGAL_PT]: pt,
    [UTSLocale.UKRAINE_UK]: uk,
    [UTSLocale.NL_NL]: nl,
    [UTSLocale.GERMANY_DE]: de,
    [UTSLocale.SWEDEN_SE]: se,
    [UTSLocale.DENMARK_DK]: da,
};

export enum ISOLanguagesCode {
    italy = "it-IT",
    spain = "es-ES",
    cat = "ca-ES",
    portugal = "pt-PT",
    france = "fr-FR",
    uk = "en-GB",
    ukraine = "uk-UA",
    us = "en-US",
}

export enum ISOLanguagesCodeByLanguage {
    it = "it-IT",
    es = "es-ES",
    ca = "ca-ES",
    pt = "pt-PT",
    fr = "fr-FR",
    en = "en-GB",
    uk = "uk-UA",
    us = "en-US",
    nl = "nl-NL",
    de = "de-DE",
    se = "sv-SE",
    da = "da-DK",
}

export enum CurrencySymbolPosition {
    BEFORE = "before",
    AFTER = "after",
}

export enum HubspotProperties {
    LANGUAGE = "impress_preferred_language",
    OPEN_PAGE = "open_page",
    SUBMIT_PAGE = "submit_page",
    SOURCE_PAGE = "source_page",
    COUNTRY = "country",
    EXPERIMENT_ID = "experiment_id",
    EXPERIMENT_GIFT = "experiment_gift",
    LEAD_TYPE = "lead_type",
    REMINDER_CONTACT_STATUS = "impress_reminders_status",
    MARKETING_CONTACT_STATUS = "impress_marketing_status",
    INSTAGRAM_USER = "instagram_user",
    LANDING_PAGE_NAME = "landing_page_name",
    JUN_2022_DISCOUNT = "jun_2022_discount_es",
    GOOGLE_ADS_ID = "hs_google_click_id",
    CREATION_SOURCE = "contact_creation_source",
    PROMOCODE = "promo_code",
    TREATMENT_TYPE = "product_type",
    RAKUTEN_SITE_ID = "rakuten_site_id",
    LEAD_FROM_DR_SMILE = "drs_leads",
    BOOKING_CHANNEL = "booking_channel",
    LEAD_ORGANIZATION = "lead_organization",
    VERIFIED_CONTACT = "verified_contact",
    REFERRAL_SOURCE = "referral_source",
    REFERRAL_PAGE = "refer_page",
}

export enum HubspotCreationSource {
    WEBSITE = "Website",
    QUIZAUGUST2022 = "QuizAugust2022",
}

export const MIN_PHONE_LETTERS = 9;

export const BOOKING_PAGE_EXCEPTIONS = ["/thanks"];

export const ThirtyMinutesInSeconds = 30 * 60;

export const TwentyFourHoursInSeconds = 60 * 60 * 24;
export const WeekInSeconds = TwentyFourHoursInSeconds * 7;

export const MonthInSeconds = 60 * 60 * 24 * 30;

export const YearInSeconds = 60 * 60 * 24 * 365;

export const TwentyFourHoursInMs = 1000 * TwentyFourHoursInSeconds;
export const MonthInMs = 1000 * MonthInSeconds;

export const MillisecondsInMinute = 1000 * 60;

export const FORTY_MINUTES_TO_MILLISECONDS = 40 * 60 * 1000;

export const QUERY_CLINIC = "clinic";
export const QUERY_EDIT = "edit";
export const BARCELONA_HIGHTLIGHT_CLINIC = 66;

export const VersionToThanksPage = {
    france: "/fr/ouverture-prochaine/",
    italy: "/it/prossima-apertura/",
    portugal: "/pt/proxima-abertura/",
    spain: "/es/proxima-apertura/",
    cat: "/es/proxima-apertura/",
    uk: "/uk/upcoming-clinic/",
    ukraine: "/ua/dyakuyu/",
};

export const VersionToNewThanksPage: any = {
    france: "/fr/merci/",
    italy: "/it/grazie/",
    portugal: "/pt/obrigado/",
    spain: "/es/gracias/",
    cat: "/es/gracias/",
    uk: "/uk/thanks/",
    ukraine: "/ua/dyakuyu/",
    us: "/us/thanks/",
};

export enum PageTheme {
    RED = "red",
    WHITE = "white",
    BLUE = "blue",
}

export enum Pages {
    About = "about",
    Locations = "locations",
    WhyImpress = "whyImpress",
    Blog = "blog",
    Press = "press",
}

export enum MFA_METHODS {
    SMS = "sms_verification",
}

export enum MFA_ERROR_CODES {
    UNSUPPORTED_METHOD = "UNSUPPORTED_METHOD",
    TOKEN_EXPIRED = "TOKEN_EXPIRED",
    INVALID_CODE = "INVALID_CODE",
    TOKEN_NOT_FOUND = "TOKEN_NOT_FOUND",
    ATTEMPTS_LIMIT = "ATTEMPTS_LIMIT",
    RETRY_TIMEOUT = "RETRY_TIMEOUT",
}

export const I18N_LOCALE = [
    "default",
    "es",
    "it",
    "pt",
    "fr",
    "ua",
    "uk",
    "us",
    "nl",
    "de",
    "se",
    "da",
];

// Here are we have commented locales, that we will start using in future
export const LOCALE_CODE = {
    italy: {
        it: "it",
        en: "en",
    },
    spain: {
        // es: "es-ES",
        cat: "ca",
        // en: "en-ES",
        es: "es",
        en: "en",
    },
    portugal: {
        // pt: "pt-PT",
        // en: "en-ES", // There are no en-PT language in Lokalise
        pt: "pt",
        en: "en",
    },
    france: {
        // fr: "fr-FR",
        // en: "en-FR",
        fr: "fr",
    },
    uk: {
        // en: "en-GB", // Should be changed to en-GB
        en: "en",
    },
    ukraine: {
        // uk: "uk-UA",
        uk: "uk",
    },
    us: {
        en: "en-US",
        es: "es-US",
    },
    nl: {
        en: "en-US",
        es: "es-US",
    },
    germany: {
        de: "de-DE",
    },
    sweden: {
        se: "sv-SE",
    },
    denmark: {
        da: "da", // TODO AD-2185: DK locale config
    },
};

export enum I18nLocale {
    SPAIN_ES = "es",
    // SPAIN_EN = "en-ES",
    ITALY_IT = "it",
    PORTUGAL_PT = "pt",
    FRANCE_FR = "fr",
    UKRAINE_UK = "ua",
    GB_EN = "uk",
    US_EN = "us",
    NL_NL = "nl",
    GERMANY_DE = "de",
    SWEDEN_SE = "se",
    DENMARK_DK = "da",
}

export enum StrapiLocale {
    SPAIN_ES = "es",
    ITALY_IT = "it",
    PORTUGAL_PT = "pt",
    FRANCE_FR = "fr",
    UKRAINE_UK = "uk",
    GB_EN = "en",
    US_EN = "en-US",
    NL_NL = "nl",
    DE_DE = "de",
    SE_SV = "sv",
    DK_DA = "da",
}

export const StrapiLocaleToI18n = {
    [StrapiLocale.SPAIN_ES]: I18nLocale.SPAIN_ES,
    [StrapiLocale.ITALY_IT]: I18nLocale.ITALY_IT,
    [StrapiLocale.PORTUGAL_PT]: I18nLocale.PORTUGAL_PT,
    [StrapiLocale.FRANCE_FR]: I18nLocale.FRANCE_FR,
    [StrapiLocale.UKRAINE_UK]: I18nLocale.UKRAINE_UK,
    [StrapiLocale.GB_EN]: I18nLocale.GB_EN,
    [StrapiLocale.US_EN]: I18nLocale.US_EN,
    [StrapiLocale.NL_NL]: I18nLocale.NL_NL,
    [StrapiLocale.DE_DE]: I18nLocale.GERMANY_DE,
    [StrapiLocale.SE_SV]: I18nLocale.SWEDEN_SE,
    [StrapiLocale.DK_DA]: I18nLocale.DENMARK_DK,
};

export const I18nToStrapiLocale = {
    [I18nLocale.SPAIN_ES]: StrapiLocale.SPAIN_ES,
    [I18nLocale.ITALY_IT]: StrapiLocale.ITALY_IT,
    [I18nLocale.PORTUGAL_PT]: StrapiLocale.PORTUGAL_PT,
    [I18nLocale.FRANCE_FR]: StrapiLocale.FRANCE_FR,
    [I18nLocale.UKRAINE_UK]: StrapiLocale.UKRAINE_UK,
    [I18nLocale.GB_EN]: StrapiLocale.GB_EN,
    [I18nLocale.US_EN]: StrapiLocale.US_EN,
    [I18nLocale.NL_NL]: StrapiLocale.NL_NL,
    [I18nLocale.GERMANY_DE]: StrapiLocale.DE_DE,
    [I18nLocale.SWEDEN_SE]: StrapiLocale.SE_SV,
    [I18nLocale.DENMARK_DK]: StrapiLocale.DK_DA,
};

export enum SentryErrorType {
    NO_RESPONSE = "Unknown error with no response",
    NO_TYPEFORM_ID = "Typeform identifier is missing",
    PAGE_NOT_FOUND = "Page not found",
    NO_CLINIC = "Clinic is missing",
    NO_SUBMIT_PAGE = "Submit page is missing",
    COMPONENT_MISSING_TYPE = "CMS component missing __component type",
    COMPONENT_TRANSFORMATION_FAILED = "CMS component transformation failed",
}

export const I18nLocaleToUTSLocale = {
    [I18nLocale.SPAIN_ES]: UTSLocale.SPAIN_ES,
    // [I18nLocale.SPAIN_EN]: UTSLocale.SPAIN_EN,
    [I18nLocale.ITALY_IT]: UTSLocale.ITALY_IT,
    [I18nLocale.PORTUGAL_PT]: UTSLocale.PORTUGAL_PT,
    [I18nLocale.FRANCE_FR]: UTSLocale.FRANCE_FR,
    [I18nLocale.UKRAINE_UK]: UTSLocale.UKRAINE_UK,
    [I18nLocale.GB_EN]: UTSLocale.GB_EN,
    [I18nLocale.US_EN]: UTSLocale.US_EN,
    [I18nLocale.NL_NL]: UTSLocale.NL_NL,
    [I18nLocale.GERMANY_DE]: UTSLocale.GERMANY_DE,
    [I18nLocale.SWEDEN_SE]: UTSLocale.SWEDEN_SE,
    [I18nLocale.DENMARK_DK]: UTSLocale.DENMARK_DK,
};

export const UTSLocaleToI18nLocale = {
    [UTSLocale.SPAIN_ES]: I18nLocale.SPAIN_ES,
    [UTSLocale.ITALY_IT]: I18nLocale.ITALY_IT,
    [UTSLocale.PORTUGAL_PT]: I18nLocale.PORTUGAL_PT,
    [UTSLocale.FRANCE_FR]: I18nLocale.FRANCE_FR,
    [UTSLocale.UKRAINE_UK]: I18nLocale.UKRAINE_UK,
    [UTSLocale.GB_EN]: I18nLocale.GB_EN,
    [UTSLocale.US_EN]: I18nLocale.US_EN,
    [UTSLocale.NL_NL]: I18nLocale.NL_NL,
    [UTSLocale.GERMANY_DE]: I18nLocale.GERMANY_DE,
    [UTSLocale.SWEDEN_SE]: I18nLocale.SWEDEN_SE,
    [UTSLocale.DENMARK_DK]: I18nLocale.DENMARK_DK,
};

export const CountryPhoneCode = {
    [UTSLocale.SPAIN_ES]: phoneCodes.ES.code,
    [UTSLocale.FRANCE_FR]: phoneCodes.FR.code,
    [UTSLocale.ITALY_IT]: phoneCodes.IT.code,
    [UTSLocale.PORTUGAL_PT]: phoneCodes.PT.code,
    [UTSLocale.GB_EN]: phoneCodes.GB.code,
    [UTSLocale.UKRAINE_UK]: phoneCodes.UA.code,
    [UTSLocale.US_EN]: phoneCodes.US.code,
    [UTSLocale.NL_NL]: phoneCodes.NL.code,
    [UTSLocale.GERMANY_DE]: phoneCodes.DE.code,
    [UTSLocale.SWEDEN_SE]: phoneCodes.SE.code,
};

export const BASE_URL = "https://smile2impress.com";

export const SITE_URLS = {
    [UTSLocale.GB_EN]: `${BASE_URL}/uk`,
    [UTSLocale.SPAIN_ES]: `${BASE_URL}/es`,
    [UTSLocale.PORTUGAL_PT]: `${BASE_URL}/pt`,
    [UTSLocale.ITALY_IT]: `${BASE_URL}/it`,
    [UTSLocale.FRANCE_FR]: `${BASE_URL}/fr`,
    [UTSLocale.UKRAINE_UK]: `${BASE_URL}/ua`,
    [UTSLocale.US_EN]: `${BASE_URL}/us`,
    [UTSLocale.NL_NL]: `${BASE_URL}/nl`,
    [UTSLocale.GERMANY_DE]: `${BASE_URL}/de`,
    [UTSLocale.SWEDEN_SE]: `${BASE_URL}/se`,
    [UTSLocale.DENMARK_DK]: `${BASE_URL}/da`,
};

export const UTSLocaleToCountryCode = {
    [UTSLocale.ITALY_IT]: CountryCode.italy,
    [UTSLocale.SPAIN_ES]: CountryCode.spain,
    [UTSLocale.PORTUGAL_PT]: CountryCode.portugal,
    [UTSLocale.FRANCE_FR]: CountryCode.france,
    [UTSLocale.GB_EN]: CountryCode.uk,
    [UTSLocale.UKRAINE_UK]: CountryCode.ukraine,
    [UTSLocale.US_EN]: CountryCode.us,
    [UTSLocale.NL_NL]: CountryCode.nl,
    [UTSLocale.GERMANY_DE]: CountryCode.germany,
    [UTSLocale.SWEDEN_SE]: CountryCode.sweden,
    [UTSLocale.DENMARK_DK]: CountryCode.denmark,
};

export const UTSLocaleToLanguage = {
    [UTSLocale.SPAIN_ES]: Language.SPAIN,
    [UTSLocale.ITALY_IT]: Language.ITALY,
    [UTSLocale.PORTUGAL_PT]: Language.PORTUGAL,
    [UTSLocale.FRANCE_FR]: Language.FRANCE,
    [UTSLocale.UKRAINE_UK]: Language.UKRAINE,
    [UTSLocale.GB_EN]: Language.ENGLISH,
    [UTSLocale.US_EN]: Language.ENGLISH,
    [UTSLocale.NL_NL]: Language.DUTCH,
    [UTSLocale.GERMANY_DE]: Language.GERMANY,
    [UTSLocale.SWEDEN_SE]: Language.SWEDEN,
    [UTSLocale.DENMARK_DK]: Language.DANISH,
};

export const CountryVersionToLocale = {
    [CountryVersion.ITALY]: I18nLocale.ITALY_IT,
    [CountryVersion.SPAIN]: I18nLocale.SPAIN_ES,
    [CountryVersion.PORTUGAL]: I18nLocale.PORTUGAL_PT,
    [CountryVersion.FRANCE]: I18nLocale.FRANCE_FR,
    [CountryVersion.UK]: I18nLocale.GB_EN,
    [CountryVersion.US]: I18nLocale.US_EN,
    [CountryVersion.NL]: I18nLocale.NL_NL,
    [CountryVersion.UKRAINE]: I18nLocale.UKRAINE_UK,
    [CountryVersion.GERMANY]: I18nLocale.GERMANY_DE,
    [CountryVersion.SWEDEN]: I18nLocale.SWEDEN_SE,
    [CountryVersion.DENMARK]: I18nLocale.DENMARK_DK,
};

export enum leadOrganizationName {
    DW = "DW",
    DR_SMILE = "DRS",
    SMAJLA = "Smajla",
}

export enum QUERY_PARAMS {
    UUID = "uuid",
    CLINIC_ID = "clinic",
}
